import Warning from "@mui/icons-material/Warning";
import { Box, Checkbox, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { ShippableContainerItemFragment, UnitType } from "generated-graphql/graphql";

import { containerTypeLabel, NO_VALUE_PLACEHOLDER } from "../Containers/containerDisplay";
import { classificationLabels } from "../Overview/Tiles/Charts/constants";
import { DeadlineChip } from "./DeadlineChip";
import { formatQuantity } from "./shipmentDisplay";
import { ShippableContainerStreamGroup } from "./shippableContainerGrouping";

/**
 * The columns every container picker uses, so the packaging wizard and the
 * add-containers dialog line up with each other rather than each inventing an
 * order. There is no waste stream column: every picker groups by stream, so it
 * would repeat the group heading on each of its rows.
 */
const CHECKBOX_COLUMN = { width: 34, flexShrink: 0 };
const LABEL_COLUMN = { width: 132, flexShrink: 0 };
const TYPE_COLUMN = { width: 150, flexShrink: 0 };
const AMOUNT_COLUMN = { width: 90, flexShrink: 0 };
const LOCATION_COLUMN = { flexGrow: 1, minWidth: 0 };

/**
 * Column labels for a container picker, matching the row beneath them.
 *
 * "Deadline" gets no fixed width and so sits flush right, matching the deadline
 * chip that ends each row: the conditional open-shipment icon before it shifts
 * the chip's left edge but never its right.
 */
export function ShippableContainerHeader() {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 1, pb: 0.5 }}>
      <Box sx={CHECKBOX_COLUMN} />
      <Caption sx={LABEL_COLUMN}>Container</Caption>
      <Caption sx={TYPE_COLUMN}>Type</Caption>
      <Caption sx={AMOUNT_COLUMN}>Amount</Caption>
      <Caption sx={LOCATION_COLUMN}>Location</Caption>
      <Caption>Deadline</Caption>
    </Stack>
  );
}

function Caption({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={sx} noWrap>
      {children}
    </Typography>
  );
}

/**
 * One container offered for packaging, shared by the packaging wizard and the
 * add-containers dialog.
 *
 * The two are the same decision reached from different entry points, so they
 * show the same facts in the same order — before this they disagreed about the
 * order of amount and location, and only one of them warned that a container was
 * already spoken for.
 */
export function ShippableContainerRow({
  container,
  checked,
  onToggle,
}: {
  container: ShippableContainerItemFragment;
  checked: boolean;
  onToggle: (id: string) => void;
}) {
  const openShipments = container.openShipments;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ px: 1, py: 0.75, borderTop: 1, borderColor: "divider" }}
    >
      <Box sx={CHECKBOX_COLUMN}>
        <Checkbox
          size="small"
          checked={checked}
          onChange={() => onToggle(container.id)}
          inputProps={{ "aria-label": `Select ${container.containerLabel}` }}
        />
      </Box>
      <Typography variant="subtitle2" sx={LABEL_COLUMN} noWrap>
        {container.containerLabel}
      </Typography>
      {/* What kind of vessel it is. The contents grid has always shown this;
          the pickers did not, so a drum and a tank read identically at the one
          moment the difference decides what happens next. */}
      <Typography variant="body2" color="text.secondary" sx={TYPE_COLUMN} noWrap>
        {containerTypeLabel(container.containerType)}
      </Typography>
      <Typography variant="body2" sx={AMOUNT_COLUMN} noWrap>
        {container.amount != null && container.amountUnit
          ? formatQuantity(container.amount, container.amountUnit)
          : NO_VALUE_PLACEHOLDER}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={LOCATION_COLUMN} noWrap>
        {container.accumulationArea?.name ?? NO_VALUE_PLACEHOLDER}
        {/* A tank does not leave with the load, it is emptied into it. That
            changes what selecting it means, so it is said where the choice is
            made rather than discovered afterwards. */}
        {container.isPermanent && (
          <Typography component="span" variant="body2" color="warning.main">
            {" · Empties the tank (stays on site)"}
          </Typography>
        )}
      </Typography>
      {/* On the row rather than in a summary underneath the list: while
          scanning checkboxes, a warning detached from its container cannot say
          which one it is about. */}
      {openShipments.length > 0 && (
        <Tooltip title={openShipmentTooltip(openShipments)}>
          <Warning color="warning" fontSize="small" aria-label="Already on an open shipment" />
        </Tooltip>
      )}
      <DeadlineChip deadline={container.clock.effectiveDeadlineDate} />
    </Stack>
  );
}

/**
 * The picker's loading state, in the same columns the rows use.
 *
 * It lives here rather than beside its caller so it cannot drift from the row
 * again: the widths above are the ones it lays out against, so a column added
 * to the row is a column the placeholder gains too. It had fallen a column
 * behind already — Type arrived on the row and never on the skeleton.
 */
export function ShippableContainerListSkeleton({
  groups = 2,
  rows = 3,
}: {
  groups?: number;
  rows?: number;
}) {
  return (
    <Box>
      {Array.from({ length: groups }).map((_, groupIndex) => (
        <Box key={groupIndex}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ py: 0.5, backgroundColor: "grey.50", px: 1 }}
          >
            <Box sx={CHECKBOX_COLUMN}>
              <Skeleton variant="rounded" width={18} height={18} />
            </Box>
            <Skeleton variant="text" width={150} />
            <Skeleton variant="rounded" width={88} height={22} />
            <Box sx={{ flexGrow: 1 }} />
            <Skeleton variant="text" width={80} />
          </Stack>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <Stack
              key={rowIndex}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ px: 1, py: 0.75, borderTop: 1, borderColor: "divider" }}
            >
              <Box sx={CHECKBOX_COLUMN}>
                <Skeleton variant="rounded" width={18} height={18} />
              </Box>
              <Skeleton variant="text" sx={LABEL_COLUMN} />
              <Skeleton variant="text" sx={TYPE_COLUMN} />
              <Skeleton variant="text" sx={AMOUNT_COLUMN} />
              <Skeleton variant="text" sx={LOCATION_COLUMN} />
              <Skeleton variant="rounded" width={44} height={24} />
            </Stack>
          ))}
        </Box>
      ))}
    </Box>
  );
}

/** "Already on open shipment MR-0003" / "…open shipments MR-0003, MR-0004". */
function openShipmentTooltip(
  openShipments: ShippableContainerItemFragment["openShipments"]
): string {
  const numbers = openShipments.map((s) => s.manifestRequestNumber);
  const plural = numbers.length === 1 ? "shipment" : "shipments";
  return `Already on open ${plural} ${numbers.join(", ")}`;
}

/**
 * Per-unit totals for a group. A stream can hold containers in different units
 * (gallons and pounds), so this reports one total per unit rather than a single
 * dimensionally-invalid sum. Containers with no recorded amount are skipped.
 */
function groupUnitTotals(
  containers: ShippableContainerItemFragment[]
): { unit: UnitType; total: number }[] {
  const totals = new Map<UnitType, number>();
  for (const c of containers) {
    if (c.amount == null || c.amountUnit == null) {
      continue;
    }
    totals.set(c.amountUnit, (totals.get(c.amountUnit) ?? 0) + c.amount);
  }
  return [...totals.entries()]
    .map(([unit, total]) => ({ unit, total }))
    .sort((a, b) => a.unit.localeCompare(b.unit));
}

/**
 * One waste stream and the containers of it that could go out: a heading that
 * can select the whole stream at once, then a row per container.
 *
 * Selecting by stream is the common case — waste leaves as a stream, and the
 * vendor manifests it that way — so it is one click rather than one per row.
 */
export function ShippableContainerGroup({
  group,
  selectedIds,
  onToggle,
  onToggleGroup,
}: {
  group: ShippableContainerStreamGroup;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleGroup: (ids: string[], selected: boolean) => void;
}) {
  const unitTotals = groupUnitTotals(group.containers);
  const ids = group.containers.map((c) => c.id);
  const allSelected = ids.every((id) => selectedIds.has(id));
  const someSelected = ids.some((id) => selectedIds.has(id));
  const streamName = group.streamName ?? "Uncharacterized";

  return (
    <Box sx={{ mb: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ py: 0.5, backgroundColor: "grey.50", px: 1 }}
      >
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={(e) => onToggleGroup(ids, e.target.checked)}
          inputProps={{ "aria-label": `Select all ${streamName} containers` }}
        />
        <Typography variant="subtitle2">{streamName}</Typography>
        {group.classification && (
          <StatusChip
            label={classificationLabels[group.classification] ?? group.classification}
            color="default"
            variant="outlined"
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {group.containers.length} cont
          {unitTotals.length > 0
            ? ` · ${unitTotals.map((t) => formatQuantity(t.total, t.unit)).join(" · ")}`
            : ""}
        </Typography>
      </Stack>
      {group.containers.map((container) => (
        <ShippableContainerRow
          key={container.id}
          container={container}
          checked={selectedIds.has(container.id)}
          onToggle={onToggle}
        />
      ))}
    </Box>
  );
}
